import { useEffect, useRef, useState } from "react";
import { Typography, Box, Divider, IconButton, Slider } from "@mui/material";
import { BASE_URL } from "../../../constants/apiConstants";
import { useTranslation } from "react-i18next";
import QuranAssessmentCard from "./QuranAssessmentCard";
const RenderImage = ({ src, alt }) => (
  <img
    src={BASE_URL + src}
    alt={alt}
    style={{
      width: 90,
      height: 90,
      borderRadius: 4,
      objectFit: "cover",
      marginBottom: 8,
    }}
  />
);

const RenderNote = ({ note, label = "note" }) => {
  const { t } = useTranslation();
  return note ? (
    <Typography variant="body2" color="text.secondary" className="mt-2">
      <strong>{t(`studentDetails.activityCard.${label}`)}:</strong> {note}
    </Typography>
  ) : null;
};

const LabeledText = ({ label, value }) =>
  value ? (
    <Typography variant="body2" color="text.secondary">
      <strong>{label}:</strong> {value}
    </Typography>
  ) : null;

const SectionHeader = ({ label, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      backgroundColor: color === "yellow" ? "#FFF8E1" : "#FCE4EC",
      borderRadius: 2,
      px: 2,
      py: 1,
      mb: 1.5,
    }}
  >
    <Typography
      variant="body1"
      fontWeight="bold"
      sx={{ color: color === "yellow" ? "#F59E0B" : "#EC407A" }}
    >
      {color === "yellow" ? "📖" : "🔄"} {label}
    </Typography>
  </Box>
);

const DetailRow = ({ iconEmoji, iconBg, title, lines = [], mistakeLines = [] }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 1.5 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1,
        backgroundColor: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: 16,
      }}
    >
      {iconEmoji}
    </Box>
    <Box>
      <Typography variant="body2" fontWeight="bold" color="text.primary">
        {title}
      </Typography>
      {lines.filter(Boolean).map((line, i) => (
        <Typography key={i} variant="body2" color="text.secondary">
          {line}
        </Typography>
      ))}
      {mistakeLines.filter(Boolean).map((line, i) => (
        <Typography key={i} variant="body2" sx={{ color: "#F59E0B" }}>
          {line}
        </Typography>
      ))}
    </Box>
  </Box>
);

const NoteRow = ({ label, value }) =>
  value ? (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 1.5, minWidth: 0 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          backgroundColor: "#E8F5E9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 16,
        }}
      >
        📝
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  ) : null;

export const ActivityCard = ({ type, details }) => {
  const { t } = useTranslation();
  switch (type) {
    case "meds":
    case "incident":
    case "note":
    case "kudos":
    case "custom": {
      const imageKeyMap = {
        meds: "MedsImage",
        incident: "IncidentImage",
        note: "NoteImage",
        kudos: "KudosImage",
        custom: "image",
      };
      const imageKey = imageKeyMap[type];

      return (
        <>
          {details[imageKey] && (
            <RenderImage src={details[imageKey]} alt={type} />
          )}
          <RenderNote note={details.Note} />
        </>
      );
    }

    case "photo":
      return (
        <div style={{ display: "grid", gap: "12px", justifyContent: "start" }}>
          {details.ActivityImage && (
            <RenderImage src={details.ActivityImage} alt="Activity visual" />
          )}
          {details.ActivityVideo && (
            <video controls style={{ width: 200, borderRadius: 4 }}>
              <source src={BASE_URL + details.ActivityVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      );

    case "quranassessment":
      return <QuranAssessmentCard details={details} />;

    case "observation":
      return (
        <>
          {details.ObservationImage && (
            <RenderImage src={details.ObservationImage} alt="Observation" />
          )}
          {details.Skill && (
            <LabeledText
              label={t("studentDetails.activityCard.skill")}
              value={details.Skill}
            />
          )}
          {details.Progress && (
            <LabeledText
              label={t("studentDetails.activityCard.progress")}
              value={details.Progress}
            />
          )}
          <RenderNote note={details.Note} />
        </>
      );

    case "checkin":
      return (
        <>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={
              details.attendance_status?.toLowerCase() === "absent"
                ? "error.main"
                : "success.main"
            }
          >
            {t("studentDetails.activityCard.status")}:{" "}
            {details.attendance_status}
          </Typography>
          <RenderNote note={details.Note} />
        </>
      );

    case "quran":
    case "recitation": {
      const isRecitation = type === "recitation";

      // ── Memorization fields ──
      const hasNewMemAya = !!details.NewAyaFrom;
      const hasNewMemSura = !!details.NewSuraFrom?.Name;
      const hasJuzMul = details.JuzMul?.length > 0;
      const hasSuraMul = details.SuraMul?.length > 0;
      const memScore = isRecitation ? details.scoreMemorization : null;
      const memTelawa = isRecitation ? details.mistakes_telawaMemorization : null;
      const memTagweed = isRecitation ? details.mistakes_tagweedMemorization : null;
      const hasMemMistakes = !!(memTelawa || memTagweed);

      const hasMemorization =
        hasNewMemAya || hasNewMemSura || hasJuzMul || hasSuraMul ||
        memScore || hasMemMistakes || !!details.Note;

      // ── Revision fields ──
      const hasRevAya = !!details.RevisionAyaFrom;
      const hasRevSura = !!details.RevisionSuraFrom?.Name;
      const hasJuzMulRevision = details.JuzMul_revision?.length > 0;
      const hasRevisionMul = details.RevisionMul?.length > 0;
      const revScore = isRecitation ? details.score : null;
      const revTelawa = isRecitation ? details.mistakes_telawa : null;
      const revTagweed = isRecitation ? details.mistakes_tagweed : null;
      const hasRevMistakes = !!(revTelawa || revTagweed);

      const hasRevision =
        hasRevAya || hasRevSura || hasJuzMulRevision || hasRevisionMul ||
        revScore || hasRevMistakes || !!details.NoteRevision;

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

          {/* ── MEMORIZATION SECTION ── */}
          {hasMemorization && (
            <Box>
              <SectionHeader
                label={t("studentDetails.activityCard.memorization")}
                color="yellow"
              />

              {/* Aya exists → "New Memorization" From/To */}
              {hasNewMemAya && (
                <DetailRow
                  iconEmoji="📗"
                  iconBg="#E8F5E9"
                  title={t("studentDetails.activityCard.newMemorization")}
                  lines={[
                    `${t("studentDetails.activityCard.from")}: ${hasNewMemSura ? `${details.NewSuraFrom.Name} (${t("studentDetails.activityCard.aya")} ${details.NewAyaFrom})` : `${t("studentDetails.activityCard.aya")} ${details.NewAyaFrom}`}`,
                    `${t("studentDetails.activityCard.to")}: ${details.NewSuraTo?.Name ? `${details.NewSuraTo.Name} (${t("studentDetails.activityCard.aya")} ${details.NewAyaTo})` : `${t("studentDetails.activityCard.aya")} ${details.NewAyaTo ?? ""}`}`,
                  ]}
                />
              )}

              {/* No Aya → separate Juz row (comma-separated) + Surahs row (comma-separated) */}
              {!hasNewMemAya && hasJuzMul && (
                <DetailRow
                  iconEmoji="📗"
                  iconBg="#E8F5E9"
                  title={t("studentDetails.activityCard.newMemorizationJuz")}
                  lines={[details.JuzMul.join(", ")]}
                />
              )}
              {!hasNewMemAya && hasSuraMul && (
                <DetailRow
                  iconEmoji="📋"
                  iconBg="#FFF8E1"
                  title={t("studentDetails.activityCard.newMemorizationSurahs")}
                  lines={[details.SuraMul.join(", ")]}
                />
              )}

              {/* Memorization Score — recitation only */}
              {memScore && (
                <DetailRow
                  iconEmoji="⭐"
                  iconBg="#EDE7F6"
                  title={t("studentDetails.activityCard.memorizationScore")}
                  lines={[String(memScore)]}
                />
              )}

              {/* Memorization Mistakes — recitation only */}
              {hasMemMistakes && (
                <DetailRow
                  iconEmoji="⚠️"
                  iconBg="#FFF3E0"
                  title={t("studentDetails.activityCard.memorizationMistakes")}
                  mistakeLines={[
                    memTelawa
                      ? `${t("studentDetails.activityCard.memorizationTelawaMistakes")}: ${memTelawa}`
                      : null,
                    memTagweed
                      ? `${t("studentDetails.activityCard.memorizationTagweedMistakes")}: ${memTagweed}`
                      : null,
                  ]}
                />
              )}

              {/* Note */}
              <NoteRow
                label={t("studentDetails.activityCard.noteMemorization")}
                value={details.Note}
              />
            </Box>
          )}

          {hasMemorization && hasRevision && <Divider />}

          {/* ── REVISION SECTION ── */}
          {hasRevision && (
            <Box>
              <SectionHeader
                label={t("studentDetails.activityCard.revision")}
                color="pink"
              />

              {/* Aya exists → "Revision" From/To */}
              {hasRevAya && (
                <DetailRow
                  iconEmoji="🔄"
                  iconBg="#E3F2FD"
                  title={t("studentDetails.activityCard.newRevision")}
                  lines={[
                    `${t("studentDetails.activityCard.from")}: ${hasRevSura ? `${details.RevisionSuraFrom.Name} (${t("studentDetails.activityCard.aya")} ${details.RevisionAyaFrom})` : `${t("studentDetails.activityCard.aya")} ${details.RevisionAyaFrom}`}`,
                    `${t("studentDetails.activityCard.to")}: ${details.RevisionSuraTo?.Name ? `${details.RevisionSuraTo.Name} (${t("studentDetails.activityCard.aya")} ${details.RevisionAyaTo})` : `${t("studentDetails.activityCard.aya")} ${details.RevisionAyaTo ?? ""}`}`,
                  ]}
                />
              )}

              {/* No Aya → separate Juz row (comma-separated) + Surahs row (comma-separated) */}
              {!hasRevAya && hasJuzMulRevision && (
                <DetailRow
                  iconEmoji="📘"
                  iconBg="#EDE7F6"
                  title={t("studentDetails.activityCard.revisionJuz")}
                  lines={[details.JuzMul_revision.join(", ")]}
                />
              )}
              {!hasRevAya && hasRevisionMul && (
                <DetailRow
                  iconEmoji="📋"
                  iconBg="#FFF8E1"
                  title={t("studentDetails.activityCard.revisionSurahs")}
                  lines={[details.RevisionMul.join(", ")]}
                />
              )}

              {/* Revision Score — recitation only */}
              {revScore && (
                <DetailRow
                  iconEmoji="⭐"
                  iconBg="#EDE7F6"
                  title={t("studentDetails.activityCard.revisionScore")}
                  lines={[String(revScore)]}
                />
              )}

              {/* Revision Mistakes — recitation only */}
              {hasRevMistakes && (
                <DetailRow
                  iconEmoji="⚠️"
                  iconBg="#FFF3E0"
                  title={t("studentDetails.activityCard.revisionMistakes")}
                  mistakeLines={[
                    revTelawa
                      ? `${t("studentDetails.activityCard.revisionTelawaMistakes")}: ${revTelawa}`
                      : null,
                    revTagweed
                      ? `${t("studentDetails.activityCard.revisionTagweedMistakes")}: ${revTagweed}`
                      : null,
                  ]}
                />
              )}

              {/* Revision Note */}
              <NoteRow
                label={t("studentDetails.activityCard.noteRevision")}
                value={details.NoteRevision}
              />
            </Box>
          )}

        </Box>
      );
    }

    default:
      return null;
  }
};